// VISUAL_OVERHAUL §"Above-the-fold structure" + A1 Patch 1 — magazine-cover
// hero with a 4-stat HighlightReel marquee instead of a single hero stat.
//
// Render order (top → bottom):
//   1. Eyebrow: "LEAGUE ALMANAC · 2023–2025"
//   2. League name in display font (Inter), scales 38-64px responsive
//   3. Thin horizontal rule
//   4. Meta line: "14 owners · 3 seasons · 504 matchups · Snake · Median Wins"
//   5. <HighlightReel> — 4-cell marquee (4-col desktop, 2x2 tablet,
//      1-col mobile) + single unifying line at the bottom

import theme from "../../data/theme";
import { pickHighlightReel } from "../../engine/heroStat";

const HERO_FONT_FAMILY = theme.fonts.almanac;

export default function AlmanacHero({ pipelineState }) {
  const stats = pipelineState?.stats;
  if (!stats) return null;

  const years = stats.meta?.yearsCovered || [];
  const yearRange = years.length === 1
    ? years[0]
    : years.length ? `${years[0]}–${years[years.length - 1]}` : "";
  const leagueName = pipelineState?.leagueMetadata?.name || "Your League";

  const metaParts = [];
  if (stats.meta?.totalOwners) metaParts.push(`${stats.meta.totalOwners} owners`);
  if (years.length) metaParts.push(`${years.length} season${years.length === 1 ? "" : "s"}`);
  if (stats.meta?.totalMatchups) metaParts.push(`${stats.meta.totalMatchups} matchups`);
  const draftType = stats.drafts?.[0]?.draftType;
  if (draftType && draftType !== "unknown") {
    metaParts.push(draftType.charAt(0).toUpperCase() + draftType.slice(1));
  }
  if (stats.meta?.formatHasMedianWins) metaParts.push("Median Wins");

  const reelStats = pickHighlightReel(stats);

  // A1 Patch 1 unifying bottom line: "{N} seasons of {league_name}."
  const unifyingLine = years.length
    ? `${years.length} season${years.length === 1 ? "" : "s"} of ${leagueName}.`
    : null;

  return (
    <section
      id="recap-section-overview"
      className="almanac-hero"
    >
      <div className="almanac-hero-eyebrow">
        League Almanac{yearRange ? ` · ${yearRange}` : ""}
      </div>
      <h1 className="almanac-hero-name" style={{ fontFamily: HERO_FONT_FAMILY }}>
        {leagueName}
      </h1>
      <div className="almanac-hero-divider" />
      <div className="almanac-hero-meta">
        {metaParts.join(" · ")}
      </div>
      {reelStats.length > 0 && (
        <HighlightReel stats={reelStats} unifyingLine={unifyingLine} />
      )}
    </section>
  );
}

// A1 Patch 1 — 4-stat marquee. 4 cols desktop, 2×2 tablet, 1 col mobile.
// Each cell: big Inter value + small label (max 2 lines). Single unifying
// line at the bottom replaces the previous flavor sentence.
function HighlightReel({ stats, unifyingLine }) {
  return (
    <div className="almanac-highlight-reel">
      <div className="almanac-highlight-eyebrow">Highlight Reel</div>
      <div className="almanac-reel-grid">
        {stats.map((s, i) => (
          <div key={i} className="almanac-reel-cell">
            <div
              className="almanac-reel-value"
              style={{ fontFamily: theme.fonts.almanac }}
            >
              {s.value}
              {s.valueSuffix && (
                <span className="almanac-reel-suffix"> {s.valueSuffix}</span>
              )}
            </div>
            <div className="almanac-reel-label">{s.label}</div>
          </div>
        ))}
      </div>
      {unifyingLine && (
        <>
          <div className="almanac-highlight-rule" />
          <div
            className="almanac-reel-tagline"
            style={{ fontFamily: theme.fonts.almanac }}
          >
            {unifyingLine}
          </div>
        </>
      )}
    </div>
  );
}
